import { Inject, Injectable } from '@angular/core';
import { SummaryDataServiceModel, SummaryResponseServiceModel } from '@src/app/core/models/summary-service.model';
import { subscriptionModelsAppConstant } from '@src/ib-subscription-models-app-constants';
import { CommonsSessionStorageService } from 'ib-web-commons/utils';
import { lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SummaryProps } from 'commercial-management--ancillaries--web-components-design--library';
import { GeneralItem, GenericSummaryItem } from 'commercial-management--ancillaries--web-components-design--library/dist/components/layout/summary/Summary.model';
import { CurrencyResponse } from '@src/app/core/models/currency.model';
import { Types } from '@src/app/core/models/configuration.model';

@Injectable({
    providedIn: 'root'
})
export class IbSubscriptionModelsSummaryService {

    private currency: CurrencyResponse;

    constructor (
        private commonsSessionStorageService: CommonsSessionStorageService,
        private translateService: TranslateService,
        @Inject('CommonsGenericProxy') private CommonsGenericProxy: any,
        @Inject('CurrencyService') private currencyService: any
    ) {
        this.currency = this.commonsSessionStorageService.get<Types.ConfigurationModel>(subscriptionModelsAppConstant.SESSION_STORAGE.CONFIGURATION_INFO).body.currency;
    }

    async getSummary (): Promise<SummaryProps> {
        const flowId: string = this.commonsSessionStorageService.get('flowId');
        const endpoint: string = subscriptionModelsAppConstant.DEFAULT_ENDPOINTS.SUMMARY.replace('{flowId}', flowId);
        const response: SummaryResponseServiceModel = await this.CommonsGenericProxy.getGeneric(endpoint);

        return await this.buildSummaryProps(response.data);
    }

    private async buildSummaryProps (data: SummaryDataServiceModel): Promise<SummaryProps> {

        const formattedAmount = this.currencyService.formatPrice(this.currency, data.voucher.price.amount, true, false);
        const amountAccountable = this.currencyService.formatPrice(this.currency, data.voucher.price.amountAccountable, true, false);

        const summaryProps: SummaryProps = {
            otherItems: await this.buildVoucherItemSummary(data, formattedAmount),
            title: await lastValueFrom(this.translateService.get('PAYMENTS_SUMMARY_TITLE')),
            totalPrice: amountAccountable,
            total: data.voucher.price.amountAccountable,
            upgradingBid: false,
            stopover: false
        };

        return summaryProps;
    }

    private async buildVoucherItemSummary (data: SummaryDataServiceModel, formattedAmount: string): Promise<any> {
        const items: GenericSummaryItem[] = [];

        const subscriptionModelsItemSummary: GenericSummaryItem = {
            type: 'SUBSCRIPTION_MODELS',
            total: formattedAmount,
            items: await this.buildSubscriptionModelsGeneralItems(data, formattedAmount)
        };

        items.push(subscriptionModelsItemSummary);
        const subscriptionModelsdDiscountItem: GenericSummaryItem = await this.getSubscriptionModelsDiscount(data);
        if(subscriptionModelsdDiscountItem) { items.push(subscriptionModelsdDiscountItem); }

        return items;
    }

    private async buildSubscriptionModelsGeneralItems (data: SummaryDataServiceModel, formattedAmount: string) : Promise<GeneralItem[]>{

        const items: GeneralItem[] = [];

        const subscriptionModelsItem: GeneralItem = {
            code: 'SM',
            description: await lastValueFrom(this.translateService.get('SUBSCRIPTION_MODELS_ANUAL_PLAN')),
            price: formattedAmount
        };

        items.push(subscriptionModelsItem);

        return items;
    }

    private async getSubscriptionModelsDiscount (data: SummaryDataServiceModel): Promise<GenericSummaryItem> {
        if (!Boolean(data.voucher.price.amountMarketing)) { return null; }
        const DISCOUNTS = await lastValueFrom(this.translateService.get('DISCOUNTS'));
        const discountAmount = data.voucher.price.amountAccountable - data.voucher.price.amount;
        const formattedDiscountAmount = this.currencyService.formatPrice(this.currency, discountAmount, true);
        const discountItem: GenericSummaryItem = {
            type: DISCOUNTS,
            total: formattedDiscountAmount,
            items: await this.getSubscriptionModelsDiscountItems(formattedDiscountAmount, data)
        };
        return discountItem;
    }

    private async getSubscriptionModelsDiscountItems (formattedDiscountAmount: string, data: SummaryDataServiceModel): Promise<GeneralItem[]> {
        const PMT_SUSBSCRIPTION_MODELS_SUMMARY_PERCENTAGE_DISCOUNT = await lastValueFrom(this.translateService.get('PMT_SUSBSCRIPTION_MODELS_SUMMARY_PERCENTAGE_DISCOUNT'));
        const formattedDiscountItemText = PMT_SUSBSCRIPTION_MODELS_SUMMARY_PERCENTAGE_DISCOUNT.replace('{{percentage}}', data.voucher.price.percentage);

        const discountItem: GeneralItem = {
            code: 'VOUCHER',
            price: formattedDiscountAmount,
            description: formattedDiscountItemText
        };

        return [discountItem];
    }
}
